// src/components/FAQ.jsx
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'How does the AI analysis work?',
    answer: 'SmartResumeTool uses Claude AI to analyze your resume against the job description. It identifies missing keywords, evaluates your qualifications match, and provides specific suggestions to improve your resume for ATS systems.'
  },
  {
    question: 'Is my resume data stored or shared?',
    answer: 'No. Your resume is processed in real-time and is never stored on our servers. All analysis happens during your session, and your data is immediately discarded after you close the page.'
  },
  {
    question: 'What file formats are supported?',
    answer: 'We support PDF (.pdf), Word documents (.docx), and plain text (.txt) files. For best results, use a cleanly formatted PDF or DOCX file.'
  },
  {
    question: 'What are ATS systems?',
    answer: 'ATS (Applicant Tracking Systems) are software tools used by employers to screen resumes. They scan for relevant keywords and qualifications before a human ever sees your application. Our tool helps optimize your resume to pass these automated screenings.'
  },
  {
    question: 'How accurate is the analysis?',
    answer: 'The analysis is powered by advanced AI and provides highly relevant suggestions. However, it should be used as a guide alongside your own judgment. Every job and company is different, so tailor the suggestions to your specific situation.'
  },
  {
    question: 'Can I analyze multiple job descriptions?',
    answer: 'Yes! You can analyze your resume against as many job descriptions as you want. Simply upload your resume once, then paste different job descriptions to see how well your resume matches each opportunity.'
  }
];

function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-0">
      <button
        onClick={onToggle}
        className="w-full py-4 px-2 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="font-medium text-gray-900 dark:text-gray-100 pr-4">
          {question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-2 pb-4 text-gray-600 dark:text-gray-400 text-sm leading-relaxed animate-fade-in">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="max-w-4xl mx-auto px-4 py-12">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
          Frequently Asked Questions
        </h2>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
          Everything you need to know about SmartResumeTool
        </p>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-sm">
            🔒 Your Privacy Matters
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
            We take your privacy seriously. Your resume and job description are processed in real-time 
            and are never stored, logged, or shared. All data is discarded immediately after analysis. 
            We use industry-standard encryption for all data transmission.
          </p>
        </div>
      </div>
    </section>
  );
}